import { shuffleArray } from "@/lib/utils";
import { useMemo, useEffect, useCallback } from "react";
import MarkdownComponent from "../Markdown";
import { colors } from "@/constant/colors";
import { useWindowSize } from "@/constant/hooks";
import useConfigStore from "@/store/useConfigStore";
import { useChatStore } from "@/store/useChatStore";

export interface MultipleChoiceProps {
  id: string;
  options: string[];
  correctAnswer: string;
  selectedAnswer?: string | null;
  answerState?: "none" | "correct" | "incorrect";
  disabled?: boolean;
  enableKeyboard?: boolean;
  enableShuffle?: boolean;
  className?: string; // 外层容器样式
  optionClassName?: string; // 选项样式
  responsive?: boolean; // 是否响应式
  canKeyboardAnswer?: boolean;
  onOptionsOrderChange?: (options: string[]) => void;
  onAnswer: (selectedOption: string, optionIndex: number, isCorrect: boolean) => void;
}

const optionLabels = ['A', 'B', 'C', 'D', 'E', 'F'];

const MultipleChoice = ({ 
  id,
  options, 
  correctAnswer, 
  selectedAnswer = null,
  answerState = "none",
  disabled = false,
  enableKeyboard = true,
  enableShuffle = true,
  onAnswer,
  className = "",
  optionClassName = "",
  responsive = true,
  canKeyboardAnswer = true,
  onOptionsOrderChange,
}: MultipleChoiceProps) => {
  const { width: windowWidth } = useWindowSize();
  const feedbackInput = useConfigStore(state => state.feedbackInput);
  const setElementDisplayState = useChatStore(state => state.setElementDisplayState);
  
  const shuffledOptions = useMemo(() => {
    const randomOptions = enableShuffle ? shuffleArray(options) : options;
    return randomOptions;
  }, [options, enableShuffle]);

  useEffect(() => {
    if (id && shuffledOptions.length > 0) {
      setElementDisplayState(id, {
        shuffledOptions: shuffledOptions
      });
    }
    onOptionsOrderChange?.(shuffledOptions);
  }, [id, onOptionsOrderChange, setElementDisplayState, shuffledOptions]);

  const handleOptionClick = useCallback((option: string, index: number) => {
    if (disabled || answerState !== "none") {
      return;
    }
    const isCorrect = option === correctAnswer;
    onAnswer(option, index, isCorrect);
  }, [disabled, answerState, correctAnswer, onAnswer]);

  // 键盘事件处理
  useEffect(() => {
    if (!enableKeyboard) return;
    if (!canKeyboardAnswer) return;
    if (feedbackInput) {
      console.log('正在输入feedbackInput', feedbackInput);
      return
    };
    
    const handleKeyPress = (event: KeyboardEvent) => {
      // 如果已经选择答案或禁用状态，不处理键盘事件
      if (disabled || answerState !== "none") {
        return;
      }

      const key = event.key.toUpperCase();
      const optionIndex = optionLabels.indexOf(key);

      // 检查按键是否有效（A-F）且选项存在
      if (optionIndex >= 0 && optionIndex < shuffledOptions.length) {
        const selectedOption = shuffledOptions[optionIndex] as string;
        handleOptionClick(selectedOption, optionIndex);
      }
    };

    // 添加键盘事件监听器
    window.addEventListener('keydown', handleKeyPress);

    // 清理事件监听器
    return () => {
      window.removeEventListener('keydown', handleKeyPress);
    };
  }, [shuffledOptions, disabled, answerState, enableKeyboard, canKeyboardAnswer, feedbackInput, handleOptionClick]);

  const getOptionStyle = (option: string, isSelected: boolean, isCorrectAnswer: boolean) => {
    let backgroundColor = "white";
    let borderColor = "#573F35";

    if (answerState !== "none") {
      if (isSelected) {
        if (answerState === "correct") {
          backgroundColor = colors.correctAnswerColorLight;
          borderColor = colors.correctAnswerColor;
        } else if (answerState === "incorrect") {
          backgroundColor = colors.wrongAnswerColorLight;
          borderColor = colors.wrongAnswerColor;
        }
      } else if (answerState === "incorrect" && isCorrectAnswer) {
        // 用户答错时，高亮正确答案
        backgroundColor = colors.correctAnswerColorLight;
        borderColor = colors.correctAnswerColor;
      }
    }

    return { backgroundColor, borderColor };
  };

  const getDefaultClassName = () => {
    if (responsive) {
      return `flex flex-col text-sm text-[#333333] font-bold rounded-xl px-4 py-2 border-1 w-full ${windowWidth > 850 ? 'w-[600px]' : 'max-w-[400px]'}`;
    }
    return "w-[300px] sm:w-[400px] md:w-[600px] text-base font-bold rounded-xl px-4 py-3 border-1";
  };

  return (
    <div className={`flex flex-col gap-3 items-center w-full ${className}`}>
      {shuffledOptions?.map((option: string, index: number) => {
        const isSelected = selectedAnswer === option;
        const isCorrectAnswer = option === correctAnswer;
        const style = getOptionStyle(option, isSelected, isCorrectAnswer);
        
        return (
          <button
            key={index}
            onClick={() => handleOptionClick(option, index)}
            disabled={disabled || answerState !== "none"}
            className={`${getDefaultClassName()} ${optionClassName}`}
            style={{
              ...style,
              cursor: disabled || answerState !== "none" ? "not-allowed" : "pointer",
              textAlign: 'left',
            }}
          >
            <span className="flex flex-row w-full items-center">
              <span 
                className="mr-4 display-text" 
                style={{ fontSize: '18px', color: colors.mainTextColor }}
              >
                {optionLabels[index]}.
              </span>
              <MarkdownComponent 
                content={option} 
                textStyle={{ 
                  textAlign: 'left', 
                  fontFamily: 'var(--font-display)', 
                  color: colors.mainTextColor, 
                  fontSize: responsive ? '16px' : '1.25rem', 
                  fontWeight: '400' 
                }} 
              />
            </span>
          </button>
        );
      })}
    </div>
  );
};  

export default MultipleChoice;
